import { useState, useRef } from 'react';

// Chip-style multi-value input. Used for skills, target roles, target
// locations, excluded companies, certifications, languages — anywhere we
// used to ship a comma-separated text input.
//
// - value: string[] (parent owns the list)
// - onChange: (next: string[]) => void
// - placeholder: text shown in the empty input
// - delimiters: characters that commit the buffer to a chip (default: Enter, comma, Tab)
// - max: optional cap (e.g. 100 for skills)
//
// Visually: chips stretch in a wrap; the input grows naturally. Each chip
// has a × to remove; backspace from an empty input deletes the last chip.
export default function ChipInput({
  value = [],
  onChange,
  placeholder = '',
  max,
  ariaLabel,
}) {
  const [buffer, setBuffer] = useState('');
  const inputRef = useRef(null);

  const commit = (raw) => {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) return;
    // Allow comma-pasting: split on commas if the buffer contains them.
    const parts = trimmed.split(/,+/).map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const next = [...value];
    for (const p of parts) {
      if (next.length >= (max ?? Infinity)) break;
      // Case-insensitive de-dupe; preserves the user's casing on first add.
      if (!next.some((x) => x.toLowerCase() === p.toLowerCase())) {
        next.push(p);
      }
    }
    onChange(next);
    setBuffer('');
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(buffer);
    } else if (e.key === 'Backspace' && buffer === '' && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    } else if (e.key === 'Tab' && buffer) {
      // Tab commits but doesn't prevent default — keep focus moving.
      commit(buffer);
    }
  };

  const handleBlur = () => {
    if (buffer.trim()) commit(buffer);
  };

  const handlePaste = (e) => {
    const txt = e.clipboardData.getData('text');
    if (txt && txt.includes(',')) {
      e.preventDefault();
      commit(txt);
    }
  };

  const removeAt = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
    inputRef.current?.focus();
  };

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      role="group"
      aria-label={ariaLabel}
      className="flex w-full flex-wrap items-center gap-1.5 rounded-md border border-slate-700 bg-slate-950/40 px-2 py-1.5 text-sm transition focus-within:border-brand-500"
    >
      {value.map((chip, i) => (
        <span
          key={`${chip}-${i}`}
          className="inline-flex items-center gap-1 rounded-full border border-brand-600/40 bg-brand-600/15 px-2 py-0.5 text-xs text-brand-500"
        >
          {chip}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeAt(i);
            }}
            aria-label={`Remove ${chip}`}
            className="rounded text-brand-500 hover:text-brand-400"
          >
            ×
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={buffer}
        onChange={(e) => setBuffer(e.target.value)}
        onKeyDown={handleKey}
        onBlur={handleBlur}
        onPaste={handlePaste}
        placeholder={value.length === 0 ? placeholder : ''}
        className="min-w-[120px] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm text-slate-100 focus:outline-none"
        aria-label={ariaLabel}
      />
    </div>
  );
}
